import React, { type ComponentProps, useCallback, useRef } from 'react';
import { ImpactFeedbackStyle } from 'expo-haptics';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import {
  Platform,
  Pressable,
  type PressableStateCallbackType,
  type StyleProp,
  StyleSheet,
  type TouchableOpacity,
  type ViewStyle,
} from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, LoadingPlaceholder, useHovered, webOnlyStyles } from '@garnish/core';
import { triggerHapticFeedback } from '@garnish/haptics';
import { ContentfulImage } from '@garnish/media';
import startCase from 'es-toolkit/compat/startCase';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuDietaryPreferencesButton = <RestrictionKind extends string>(
  props: MenuDietaryPreferencesButtonProps<RestrictionKind>,
) => {
  const {
    icon,
    restriction,
    isChecked,
    isDisabled,
    isLoading,
    style,
    toggleRestriction,
    ...restProps
  } = props;

  const ref = useRef(null);
  const isHovered = useHovered(ref);
  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleToggleRestriction = useCallback(() => {
    if (isDisabled) return;

    toggleRestriction(restriction);
    void triggerHapticFeedback(ImpactFeedbackStyle.Light);
  }, [isDisabled, restriction, toggleRestriction]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const hasSelectedStyle = isHovered || isChecked;

  const labelStyles = [
    styles.label,
    labelWebStyles,
    hasSelectedStyle ? styles.labelHover : styles.labelIdle,
  ];

  const iconColor = hasSelectedStyle ? theme.colors.CREAM : theme.colors.DARK_KALE;

  const pressableStyles = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const { pressed } = state;

      return [
        styles.pressable,
        pressableWebStyles,
        isChecked ? styles.pressableChecked : undefined,
        isHovered && !isChecked ? styles.pressableHover : undefined,
        isHovered && isChecked ? styles.pressableCheckedHover : undefined,
        pressed ? styles.pressablePressed : undefined,
        style,
      ];
    },
    [isChecked, isHovered, style],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingPlaceholder palette="cream" columnWidth={100} rowHeight={42} />;
  }

  // ─────────────────────────────────────────────────────────────────────

  const name = startCase(restriction.toLowerCase().replaceAll('_', ' '));
  const accessibilityLabel = isChecked
    ? formatMessage(messages.deselectRestrictionA11yLabel, { restriction: name })
    : formatMessage(messages.selectRestrictionA11yLabel, { restriction: name });

  return (
    <Pressable
      ref={ref}
      style={pressableStyles}
      accessibilityState={{ checked: isChecked, disabled: isDisabled }}
      accessibilityRole="checkbox"
      accessibilityLabel={accessibilityLabel}
      onPress={handleToggleRestriction}
      {...Platform.select({ web: { accessibilityChecked: isChecked } })}
      {...restProps}
    >
      {icon ? (
        <ContentfulImage
          style={styles.icon}
          tintColor={iconColor}
          baseUrl={icon}
          options={iconConfig}
        />
      ) : null}

      <BodyText style={labelStyles} sizeMatch={['18']} numberOfLines={1}>
        {name}
      </BodyText>
    </Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly selectRestrictionA11yLabel: { readonly restriction: MessageValue };
  readonly deselectRestrictionA11yLabel: { readonly restriction: MessageValue };
}>({
  selectRestrictionA11yLabel: {
    defaultMessage: 'Select {restriction}',
    description: 'Menu | Dietary preferences | Select restriction a11y label',
  },
  deselectRestrictionA11yLabel: {
    defaultMessage: 'Deselect {restriction}',
    description: 'Menu | Dietary preferences | Deselect restriction a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const iconConfig: ContentfulImageOptions = {
  w: 20,
  h: 20,
  fm: 'webp',
};

const styles = StyleSheet.create({
  pressable: {
    flexDirection: 'row',
    columnGap: 6,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.SPINACH.LIGHTEST,
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['4'],
    minHeight: 24,
    userSelect: 'none',
  },
  pressableHover: {
    backgroundColor: theme.colors.KALE,
  },
  pressableChecked: {
    backgroundColor: theme.colors.KALE,
  },
  pressableCheckedHover: {
    backgroundColor: theme.colors.KALE_HOVER,
  },
  pressablePressed: {
    opacity: 0.7,
  },
  icon: {
    width: 20,
    height: 20,
  },
  label: {
    textAlign: 'center',
    textTransform: 'capitalize',
  },
  labelIdle: {
    color: theme.colors.TEXT_COLOR,
  },
  labelHover: {
    color: theme.colors.CREAM,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: `background-color ${theme.transitions.base}ms, opacity ${theme.transitions.base}ms`,
});
const labelWebStyles = webOnlyStyles({
  transition: `color ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentfulImageOptions = ComponentProps<typeof ContentfulImage>['options'];

type MenuDietaryPreferencesButtonProps<RestrictionKind> = {
  icon?: string;
  restriction: RestrictionKind;
  isDisabled: boolean;
  isLoading: boolean;
  isChecked: boolean;
  toggleRestriction: (restriction: RestrictionKind) => void;
  style?: StyleProp<ViewStyle>;
} & Omit<
  ComponentProps<typeof TouchableOpacity>,
  | 'children'
  | 'ref'
  | 'style'
  | 'accessibilityLabel'
  | 'aria-label'
  | 'aria-checked'
  | 'accessibilityState'
  | 'accessibilityRole'
  | 'onPress'
>;
